<template>
  <!-- Bulle réduite -->
  <button
    type="button"
    class="fixed bottom-5 right-5 z-[65] flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl transition hover:bg-blue-700"
    aria-label="Ouvrir les messages"
    @click="openWidget"
  >
    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a8 8 0 01-8 8H7l-4 3v-4.6A8 8 0 1121 12z" /></svg>
    <span v-if="unreadCount" class="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
  </button>

  <!-- Fenêtre Messenger -->
  <div
    v-if="open"
    class="fixed bottom-[5.5rem] right-5 z-[65] flex w-[calc(100vw-2.5rem)] max-w-[360px] flex-col overflow-hidden rounded-t-2xl border border-gray-300 bg-gray-100 shadow-2xl sm:bottom-24 lg:max-w-[380px]"
    style="height: min(560px, calc(100vh - 12rem));"
  >
    <!-- Vue Assistant IA (aucun socket de frappe ni accusé de lecture) -->
    <AiAssistantPanel v-if="aiOpen" @back="aiOpen = false" @close="closeWidget" />

    <!-- Vue liste des conversations -->
    <template v-else-if="!activeChannel">
      <header class="flex items-center justify-between gap-2 bg-gray-200 px-3 py-2.5">
        <span class="flex items-center gap-2 text-base font-bold text-gray-900">
          Messages
          <span v-if="unreadCount" class="rounded-full bg-red-600 px-1.5 py-0.5 text-[11px] font-bold leading-4 text-white">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </span>
        <div class="flex items-center gap-0.5">
          <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Nouveau groupe" @click="openModal('group')">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M17 20H7m10 0v-2c0-.66-.34-1.2-.85-1.61M17 20v-2a4 4 0 00-4-4H7M12 4a4 4 0 110 8 4 4 0 010-8z" /></svg>
          </button>
          <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Nouveau message privé" @click="openModal('direct')">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
          </button>
          <button
            type="button"
            class="rounded-full p-1.5"
            :class="isSoundEnabled('message') ? 'text-gray-600 hover:bg-gray-300' : 'text-gray-400 hover:bg-gray-300'"
            :title="isSoundEnabled('message') ? 'Couper le son des messages' : 'Activer le son des messages'"
            @click="toggleSound('message')"
          >
            <svg v-if="isSoundEnabled('message')" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5L6 9H3v6h3l5 4V5z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" />
            </svg>
            <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5L6 9H3v6h3l5 4V5z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M22 9l-6 6M16 9l6 6" />
            </svg>
          </button>
          <button
            type="button"
            class="rounded-full p-1.5"
            :class="isSoundEnabled('notification') ? 'text-gray-600 hover:bg-gray-300' : 'text-gray-400 hover:bg-gray-300'"
            :title="isSoundEnabled('notification') ? 'Couper le son des notifications' : 'Activer le son des notifications'"
            @click="toggleSound('notification')"
          >
            <svg v-if="isSoundEnabled('notification')" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17H9m9 0a2 2 0 002-2V9a6 6 0 10-12 0v6a2 2 0 002 2m1 0v1a4 4 0 008 0v-1" />
            </svg>
            <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17H9m9 0a2 2 0 002-2V9a6 6 0 10-12 0v6a2 2 0 002 2m1 0v1a4 4 0 008 0v-1" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />
            </svg>
          </button>
          <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Réduire" @click="minimizeWidget">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14" /></svg>
          </button>
          <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Fermer" @click="closeWidget">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </header>

      <div class="flex-1 overflow-y-auto bg-white">
        <button
          v-if="canViewAi"
          type="button"
          class="flex w-full items-center gap-3 border-b border-gray-200 bg-blue-50 px-3 py-2.5 text-left hover:bg-blue-100"
          @click="aiOpen = true"
        >
          <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">IA</span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-semibold text-gray-900">Assistant IA</span>
            <span class="block truncate text-sm text-gray-500">Poser une question</span>
          </span>
        </button>
        <p v-if="!channels.length" class="p-6 text-center text-sm text-gray-500">Aucune discussion.</p>
        <button
          v-for="channel in channels"
          :key="channel.id"
          type="button"
          class="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-100"
          @click="selectChannel(channel.id)"
        >
          <span class="relative self-center shrink-0">
            <span class="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-gray-300 text-sm font-semibold text-gray-700">
              <img v-if="photoOf(channel)" :src="photoOf(channel)!" class="h-full w-full object-cover" alt="" />
              <template v-else>{{ initials(channelName(channel)) }}</template>
            </span>
            <span v-if="channel.unreadCount" class="absolute -bottom-0.5 -right-0.5 min-w-5 rounded-full bg-blue-600 px-1 text-center text-[10px] font-bold leading-5 text-white">{{ channel.unreadCount > 99 ? '99+' : channel.unreadCount }}</span>
          </span>
          <span class="min-w-0 flex-1">
            <span class="flex items-baseline justify-between gap-2">
              <span class="min-w-0 truncate text-sm font-semibold text-gray-900">{{ channelName(channel) }}</span>
              <span class="shrink-0 text-[11px] text-gray-500">{{ channel.lastMessage ? formatTime(channel.lastMessage.createdAt) : '' }}</span>
            </span>
            <span class="mt-0.5 block truncate text-sm text-gray-500">
              <span class="flex items-center gap-1">
                <svg
                  v-if="channel.lastMessage && channel.lastMessage.senderId === currentUserId"
                  class="h-3.5 w-3.5 shrink-0"
                  :class="deliveryState(channel.lastMessage) === 'READ' ? 'text-blue-500' : 'text-gray-400'"
                  :title="deliveryLabel(channel.lastMessage)"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path v-if="deliveryState(channel.lastMessage) === 'SENT'" stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  <template v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M1.5 13.5l4 4 8.5-8.5" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 12l3 3 8-8" />
                  </template>
                </svg>
                <template v-if="typingInChannel(channel.id).length">
                  <span class="truncate font-medium text-blue-600">{{ typingLabel(typingInChannel(channel.id)) }}</span>
                  <span class="typing-dots shrink-0" :class="activeChannelId === channel.id ? 'typing-dots-active' : ''" aria-label="est en train d'écrire"><i /><i /><i /></span>
                </template>
                <span v-else class="truncate">
                  <span v-if="channel.lastMessage" class="font-semibold text-gray-700">{{ channel.lastMessage.senderId === currentUserId ? 'Vous : ' : '' }}</span>{{ channel.lastMessage?.body || 'Démarrer la discussion' }}
                </span>
              </span>
            </span>
          </span>
        </button>
      </div>
    </template>

    <!-- Vue conversation -->
    <template v-else>
      <header class="flex items-center gap-2 bg-gray-200 px-2.5 py-2.5">
        <button type="button" class="rounded-full p-1 text-gray-700 hover:bg-gray-300" title="Retour" @click="backToList">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <span class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-300 text-xs font-semibold text-gray-700">
          <img v-if="photoOf(activeChannel)" :src="photoOf(activeChannel)!" class="h-full w-full object-cover" alt="" />
          <template v-else>{{ initials(channelName(activeChannel)) }}</template>
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-bold text-gray-900">{{ channelName(activeChannel) }}</span>
          <span class="block truncate text-[11px] text-gray-600">{{ activeChannel.type === 'DIRECT' ? 'Discussion privée' : `Groupe · ${activeChannel.members.length} membres` }}</span>
        </span>
        <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Réduire" @click="minimizeWidget">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14" /></svg>
        </button>
        <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Fermer" @click="closeWidget">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </header>

      <div ref="messagesEl" class="flex-1 space-y-1 overflow-y-auto bg-gray-100 px-3 py-3" @scroll.passive="onMessagesScroll">
        <div class="flex items-center justify-center py-1.5">
          <span v-if="loadingOlder" class="flex items-center gap-2 text-xs text-gray-400">
            <span class="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500"></span>
            Chargement...
          </span>
          <button v-else-if="hasMore" type="button" class="rounded-full bg-white px-3 py-1 text-xs font-medium text-blue-600 shadow-sm" @click="loadMore">Charger plus ancien</button>
        </div>
        <p v-if="!messages.length" class="py-6 text-center text-sm text-gray-500">Aucun message.</p>
        <div
          v-for="message in visibleMessages"
          :key="message.id"
          class="relative flex items-end gap-2"
          :class="message.senderId === currentUserId ? 'justify-end' : 'justify-start'"
          @mouseleave="closePanels"
        >
          <span
            v-if="message.senderId !== currentUserId"
            class="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-300 text-[10px] font-semibold text-gray-700"
          >
            <img v-if="message.sender?.photoUrl" :src="photoUrl(message.sender.photoUrl)" class="h-full w-full object-cover" alt="" />
            <template v-else>{{ initials(message.sender?.name || '?') }}</template>
          </span>
          <div
            class="max-w-[78%]"
            :class="message.senderId === currentUserId ? 'order-2' : 'order-1'"
          >
            <p
              v-if="message.senderId !== currentUserId && activeChannel.type === 'GROUP'"
              class="mb-0.5 px-1 text-[11px] font-semibold text-gray-600"
            >{{ message.sender?.name }}</p>
            <div class="group relative">
              <div
                class="px-3 py-2 text-sm leading-5 shadow-sm"
                :class="[
                  message.senderId === currentUserId
                    ? 'rounded-br-md rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                    : 'rounded-bl-md rounded-2xl bg-white text-gray-900',
                  isHidden(message) ? 'italic text-gray-500' : '',
                ]"
              >
                <p v-if="isHidden(message)" class="whitespace-pre-wrap break-words">Message supprimé</p>
                <p v-else class="whitespace-pre-wrap break-words">{{ message.body }}</p>
                <p
                  v-if="message.senderId === currentUserId && !isHidden(message)"
                  class="mt-0.5 flex justify-end"
                  :title="deliveryLabel(message)"
                >
                  <svg
                    v-if="deliveryState(message) === 'SENT'"
                    class="h-3.5 w-3.5 opacity-60"
                    fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <svg
                    v-else
                    class="h-3.5 w-3.5"
                    :class="deliveryState(message) === 'READ' ? 'text-sky-100' : 'opacity-60'"
                    fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M1.5 13.5l4 4 8.5-8.5" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 12l3 3 8-8" />
                  </svg>
                </p>
                
                <p v-if="message.isEphemeral" class="mt-1 flex items-center gap-1 text-[10px] opacity-80">
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Vue une fois
                </p>
                <!-- Reactions inline en bas de la bulle -->
                <div
                  v-if="!isHidden(message) && reactionGroups(message).length"
                  class="mt-1.5 flex flex-wrap items-center gap-1"
                >
                  <button
                    v-for="group in reactionGroups(message)"
                    :key="group.emoji"
                    type="button"
                    class="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs transition"
                    :class="group.mine
                      ? 'bg-blue-100 ring-1 ring-blue-300'
                      : 'bg-gray-100 hover:bg-gray-200'"
                    :title="reactionTitle(group)"
                    @click.stop="toggleReaction(message.id, group.emoji)"
                  >
                    <span>{{ group.emoji }}</span>
                    <span :class="group.mine ? 'font-semibold text-blue-700' : 'text-gray-600'">{{ group.count }}</span>
                  </button>
                </div>
              </div>
              <button
                v-if="message.senderId === currentUserId && !message.deletedAt"
                type="button"
                class="absolute -top-2 hidden h-6 w-6 items-center justify-center rounded-full bg-white text-gray-500 shadow ring-1 ring-gray-200 hover:text-red-600 group-hover:flex"
                title="Supprimer le message"
                @click="removeMessage(message)"
              >
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
              <!-- Horodatage hors flux : sans cela il gonflerait la hauteur de
                   la colonne et le smiley se centrerait sur la bulle plus
                   l heure, donc trop bas. -->
              <p
                class="absolute top-full mt-0.5 px-1 text-[10px] text-gray-500"
                :class="message.senderId === currentUserId ? 'right-0 text-right' : 'left-0'"
              >{{ formatTime(message.createdAt) }}<span v-if="message.editedAt"> · modifié</span></p>
            </div>
          </div>
            <!-- Bouton smiley : a cote de la bulle et centre sur elle.
                 L ordre est porte par flex order, donc du cote du centre
                 horizontal du widget selon l expediteur. -->
            <div
              v-if="!isHidden(message)"
              class="relative self-center shrink-0"
              :class="message.senderId === currentUserId ? 'order-1' : 'order-2'"
            >
              <div class="flex shrink-0 flex-col items-start">
                <div class="relative">
                  <button
                    type="button"
                    class="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                    title="Réagir"
                    @click.stop="togglePanels(message.id)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /><path d="M9.75 9.75h.008v.015H9.75V9.75Zm4.5 0h.008v.015h-.008V9.75Z" /></svg>
                  </button>

                  
                </div>


              </div>
            </div>

            <!-- Les deux panneaux sont enfants directs de la ligne :
                 elle occupe toute la largeur du widget, donc un centrage
                 par left-1/2 se fait sur le widget et non sur le bouton.
                 Ancre dans le bouton, ils deborderaient du cote oppose. -->
            <!-- Les deux panneaux sont enfants directs de la ligne : elle occupe
                 toute la largeur du widget, donc un centrage par left-1/2 se
                 fait sur le widget et non sur le bouton. Ancre dans le bouton,
                 ils deborderaient du cote oppose.
                 Ils sont positionnes au-dessus de la ligne, donc y entrer
                 declenche un mouseleave sur elle : c est pourquoi chacun porte
                 ses propres handlers, sans quoi la liste se fermerait dès que
                 le curseur monterait vers les emojis. -->
            <div
              v-if="quickBarFor === message.id"
              class="absolute bottom-full left-1/2 z-20 mb-1 flex -translate-x-1/2 gap-0.5 rounded-full bg-white p-1 shadow-lg ring-1 ring-gray-200"
              @click.stop
              @mouseenter="cancelClose"
              @mouseleave="closePanels"
            >
              <button
                v-for="e in QUICK_REACTIONS"
                :key="e"
                type="button"
                class="rounded-full px-1.5 py-0.5 text-base hover:bg-gray-100"
                @click="toggleReaction(message.id, e)"
              >{{ e }}</button>
            </div>

            <!-- Selecteur complet : centre comme la barre rapide, sinon ses
                 224 px sortiraient du widget pour un message recu. -->
            <div
              v-if="pickerFor === message.id && !isHidden(message)"
              class="absolute bottom-full left-1/2 z-20 mb-1 max-h-40 w-56 -translate-x-1/2 overflow-y-auto rounded-lg bg-white p-2 shadow-lg ring-1 ring-gray-200"
              @click.stop
              @mouseenter="cancelClose"
              @mouseleave="closePanels"
            >
              <div class="grid grid-cols-8 gap-0.5">
                <button
                  v-for="e in EMOJIS"
                  :key="e"
                  type="button"
                  class="rounded p-0.5 text-lg hover:bg-gray-100"
                  @click="toggleReaction(message.id, e)"
                >{{ e }}</button>
              </div>
            </div>
          </div>

      </div>

      <p
        v-if="activeTypingNames.length"
        class="flex items-center gap-1.5 px-1 pb-1 text-xs font-medium text-gray-500"
      >
        <span class="truncate">{{ typingLabel(activeTypingNames) }}</span>
        <span class="typing-dots" aria-label="est en train d'écrire"><i /><i /><i /></span>
      </p>

      <form class="relative bg-gray-200 px-3 py-2.5" @submit.prevent="send">
        <!-- Picker emoji -->
        <div v-if="emojiOpen" class="absolute bottom-full left-3 right-3 z-20 mb-2 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
          <div class="grid grid-cols-8 gap-0.5">
            <button
              v-for="emoji in EMOJIS"
              :key="emoji"
              type="button"
              class="rounded p-1 text-xl leading-6 hover:bg-gray-100"
              @click="insertEmoji(emoji)"
            >{{ emoji }}</button>
          </div>
          <div class="mt-2 flex justify-end border-t border-gray-100 pt-2">
            <button type="button" class="text-xs font-medium text-gray-500 hover:text-gray-800" @click="emojiOpen = false">Fermer</button>
          </div>
        </div>

        <!-- Champ de saisie : hauteur automatique, actions a l interieur -->
        <div class="flex items-end gap-1 rounded-3xl bg-white px-3 py-1.5 shadow-sm">
          <textarea
            ref="draftEl"
            v-model="draft"
            rows="1"
            placeholder="Aa"
            class="max-h-28 flex-1 resize-none border-0 bg-transparent py-1.5 text-sm leading-5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
            @input="autoGrow(); notifyTyping()"
            @keydown.enter.exact.prevent="send"
          ></textarea>

          <div class="flex shrink-0 items-center gap-0.5 pb-0.5">
            <button
              type="button"
              class="rounded-full p-1.5 transition"
              :class="ephemeral ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'"
              :title="ephemeral ? 'Vue une fois active' : 'Vue une fois : le message disparait apres lecture'"
              @click="ephemeral = !ephemeral"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </button>
            <button
              type="button"
              class="rounded-full p-1.5 transition"
              :class="emojiOpen ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'"
              title="Émoji"
              @click="toggleEmoji"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 9v2a2 2 0 004 0V9m-4 0H9.5A4.5 4.5 0 0013 5.5M9 13.5s1.5 2 4 2m-9 3.5a9 9 0 1018 0 9 9 0 00-18 0z" /></svg>
            </button>
            <button
              type="submit"
              :disabled="sending || !draft.trim()"
              class="rounded-full p-1.5 text-blue-600 transition hover:bg-blue-50 disabled:opacity-30"
              title="Envoyer"
            >
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M2.5 21l21-9L2.5 3v7l15 2-15 2v7z" /></svg>
            </button>
          </div>
        </div>
      </form>
    </template>

    <!-- Modale création -->
    <div v-if="modalOpen" class="absolute inset-0 z-10 flex items-center justify-center bg-black/50 p-4" @click.self="modalOpen = false">
      <div class="w-full max-w-xs rounded-xl bg-white p-4 shadow-xl">
        <h3 class="text-sm font-bold text-gray-900">{{ modalMode === 'group' ? 'Nouveau groupe' : 'Nouveau message privé' }}</h3>
        <label v-if="modalMode === 'group'" class="mt-3 block text-xs font-medium text-gray-700">Nom du groupe</label>
        <input v-if="modalMode === 'group'" v-model="groupName" type="text" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="Ex. Équipe logistique" />
        <p class="mt-3 text-xs font-medium text-gray-700">Membres</p>
        <input v-model="employeeQuery" type="search" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="Rechercher…" />
        <div class="mt-2 max-h-40 space-y-0.5 overflow-y-auto">
          <button v-for="employee in selectableEmployees" :key="employee.id" type="button" class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-gray-100" @click="toggleEmployee(employee.id)">
            <span class="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700">
              <img v-if="employee.photoUrl" :src="photoUrl(employee.photoUrl)" class="h-full w-full object-cover" alt="" />
              <template v-else>{{ initials(employee.name) }}</template>
            </span>
            <span class="min-w-0 flex-1 truncate text-gray-900">{{ employee.name }}</span>
            <span v-if="selectedEmployees.includes(employee.id)" class="text-xs font-bold text-blue-600">✓</span>
          </button>
          <p v-if="!selectableEmployees.length" class="p-2 text-xs text-gray-500">Aucun employé</p>
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <button type="button" class="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100" @click="modalOpen = false">Annuler</button>
          <button type="button" class="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50" :disabled="creating" @click="createChannel">Créer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { showToast } from '@/lib/ui'
import { apolloClient } from '@/lib/apollo'
import { CHAT_CHANNELS_QUERY, CHAT_MESSAGES_QUERY, CREATE_CHAT_CHANNEL, MARK_CHAT_CHANNEL_READ, SEND_CHAT_MESSAGE, DELETE_CHAT_MESSAGE, TOGGLE_CHAT_REACTION } from '@/graphql/chat'
import { EMPLOYEES_QUERY } from '@/graphql/employee'
import { useMessenger } from '@/lib/messenger'
import { usePermissionsStore } from '@/stores/permissions'
import AiAssistantPanel from './AiAssistantPanel.vue'
import { isSoundEnabled, playMessageSound, playNotificationSound, toggleSound } from '@/lib/sound'

const authStore = useAuthStore()
const BACKEND_URL = import.meta.env.VITE_GRAPHQL_URL?.replace('/graphql', '') || 'http://localhost:4000'
const currentUserId = computed(() => authStore.user?.id || '')

const permissionsStore = usePermissionsStore()
const canViewAi = computed(() => permissionsStore.hasPermission('assistant-ia', 'view'))
const aiOpen = ref(false)

const messenger = useMessenger()
const open = messenger.open
const channels = ref<any[]>([])
const activeChannelId = ref<number | null>(null)
const messages = ref<any[]>([])
const hasMore = ref(false)
const draft = ref('')
const draftEl = ref<HTMLTextAreaElement | null>(null)
const emojiOpen = ref(false)
const ephemeral = ref(false)
const sending = ref(false)
const unreadCount = ref(0)
const messagesEl = ref<HTMLElement | null>(null)

const modalOpen = ref(false)
const modalMode = ref<'group' | 'direct'>('group')
const groupName = ref('')
const employeeQuery = ref('')
const selectedEmployees = ref<string[]>([])
const creating = ref(false)
const employees = ref<any[]>([])
const loadEmployees = async () => {
  const data: any = await apolloClient.query({ query: EMPLOYEES_QUERY, fetchPolicy: 'cache-first' }).catch(() => null)
  if (data?.data?.employees) employees.value = data.data.employees
}

const photoUrl = (url?: string | null) => {
  if (!url) return null
  const token = authStore.token
  return token ? `${BACKEND_URL}${url}?token=${encodeURIComponent(token)}` : `${BACKEND_URL}${url}`
}
const initials = (value: string) => value.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?'
import { formatTime } from '../utils/date'

const channelName = (channel: any) => {
  if (channel.type !== 'DIRECT') return channel.name || 'Groupe'
  const other = channel.members?.find((m: any) => m.employeeId !== currentUserId.value)
  return other?.employee?.name || 'Discussion'
}
const photoOf = (channel: any) => {
  if (channel?.type !== 'DIRECT') return null
  const other = channel.members?.find((m: any) => m.employeeId !== currentUserId.value)
  return photoUrl(other?.employee?.photoUrl)
}

// Un message supprime est masque pour le destinataire ; l auteur garde sa copie.
const isHidden = (message: any) => !!message.deletedAt && message.senderId !== currentUserId.value

const removeMessage = async (message: any) => {
  try {
    await apolloClient.mutate({ mutation: DELETE_CHAT_MESSAGE, variables: { id: message.id } })
    await loadMessages(activeChannelId.value!)
    await loadChannels()
  } catch {
    showToast({ type: 'error', message: 'Suppression impossible' })
  }
}

const EMOJIS = [
  '\u{1F600}', '\u{1F603}', '\u{1F604}', '\u{1F601}', '\u{1F606}', '\u{1F605}', '\u{1F923}', '\u{1F609}', '\u{1F60A}', '\u{1F607}', '\u{1F970}', '\u{1F60D}', '\u{1F929}', '\u{1F618}', '\u{1F617}', '\u{1F61A}', '\u{1F619}', '\u{1F60B}', '\u{1F61B}', '\u{1F61C}', '\u{1F642}', '\u{1F643}', '\u{1F44D}', '\u{1F44E}', '\u{1F44C}', '\u{1F44F}', '\u{1F450}', '\u{1F64C}', '\u{1F64F}', '\u{1F440}', '\u{1F446}', '\u{1F447}', '\u{1F44B}', '\u{1F44A}', '\u{1F590}\u{FE0F}', '\u{2764}\u{FE0F}', '\u{1F495}', '\u{1F496}', '\u{1F497}', '\u{1F498}', '\u{1F49D}', '\u{1F49B}', '\u{1F4A5}', '\u{1F4AA}', '\u{1F4AC}', '\u{1F4AF}', '\u{1F525}', '\u{2728}', '\u{1F389}', '\u{1F4A1}', '\u{1F4E5}', '\u{1F4C8}', '\u{2705}', '\u{274C}', '\u{26A0}\u{FE0F}', '\u{1F4DD}', '\u{1F680}', '\u{1F41B}', '\u{1F4C5}', '\u{1F4CA}', '\u{1F4C6}',
]

const toggleEmoji = () => { emojiOpen.value = !emojiOpen.value }

const MAX_DRAFT_HEIGHT = 112 // ~7rem, aligne sur max-h-28

// Le champ grandit avec le texte et se retranscrit quand le message raccourcit.
const autoGrow = () => {
  const el = draftEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, MAX_DRAFT_HEIGHT)}px`
  el.style.overflowY = el.scrollHeight > MAX_DRAFT_HEIGHT ? 'auto' : 'hidden'
}

// couvre la saisie programmee : insertion d emoji, effacement apres envoi, collage
watch(draft, () => nextTick(autoGrow))

// insere l emoji a la position du curseur, sans ecraser la saisie en cours
const insertEmoji = (emoji: string) => {
  const el = draftEl.value
  if (!el) { draft.value += emoji; return }
  const start = el.selectionStart ?? draft.value.length
  const end = el.selectionEnd ?? start
  draft.value = draft.value.slice(0, start) + emoji + draft.value.slice(end)
  nextTick(() => {
    el.focus()
    const caret = start + emoji.length
    el.setSelectionRange(caret, caret)
  })
}

// Etat de livraison d un de mes messages : envoyee -> vue -> lu.
// On retient l etat le plus avance atteint par au moins un destinataire.
// Reactions
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏']
const quickBarFor = ref<number | null>(null)
const pickerFor = ref<number | null>(null)
let quickBarTimer: ReturnType<typeof setTimeout> | undefined

const reactionGroups = (message: any) => {
  const list = (message.reactions ?? []) as { id: number; employeeId: string; emoji: string }[]
  const byEmoji = new Map<string, { count: number; mine: boolean }>()
  for (const r of list) {
    const g = byEmoji.get(r.emoji) ?? { count: 0, mine: false }
    g.count += 1
    if (r.employeeId === currentUserId.value) g.mine = true
    byEmoji.set(r.emoji, g)
  }
  return [...byEmoji.entries()]
    .map(([emoji, g]) => ({ emoji, ...g }))
    .sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji))
}

const reactionTitle = (group: { emoji: string; count: number; mine: boolean }) => {
  if (group.count === 1) return group.mine ? 'Votre réaction' : '1 réaction'
  return group.mine ? `Votre réaction et ${group.count - 1} autre(s)` : `${group.count} réactions`
}

// Un clic sur le bouton ouvre la barre rapide. Un second clic ouvre le
// selecteur complet. Les deux se ferment si on reclique, ou en sortant de la
// ligne du message.
const togglePanels = (messageId: number) => {
  if (pickerFor.value === messageId) {
    closePanels()
    return
  }
  if (quickBarFor.value === messageId) {
    pickerFor.value = messageId
    return
  }
  clearTimeout(quickBarTimer)
  quickBarFor.value = messageId
}

// Le panneau est affiche au-dessus de la ligne du message : y entrer declenche
// un mouseleave sur elle. Sans annulation, la fermeture partait avant que le
// curseur n atteigne les emojis.
const cancelClose = () => {
  clearTimeout(quickBarTimer)
}

const closePanels = () => {
  clearTimeout(quickBarTimer)
  // delai volontairement large : le pointeur doit traverser l espace entre la
  // ligne et le panneau, qui se trouve au-dessus d elle
  quickBarTimer = setTimeout(() => {
    quickBarFor.value = null
    pickerFor.value = null
  }, 400)
}

const toggleReaction = async (messageId: number, emoji: string) => {
  quickBarFor.value = null
  pickerFor.value = null
  try {
    const { data } = await apolloClient.mutate({
      mutation: TOGGLE_CHAT_REACTION,
      variables: { messageId, emoji },
    })
    const payload = (data as any).toggleChatMessageReaction
    if (!payload) throw new Error('reponse vide')
    // on ne touche que le message concerne : pas de rechargement global
    applyReactions(messageId, payload.reactions)
  } catch (e) {
    // Avant, l erreur etait avalee en silence : rien ne se passait a l ecran
    // et il fallait rouvrir la conversation pour voir l etat reel. On le
    // signale, et on relit le message pour se resynchroniser.
    console.warn('[chat] reaction non appliquee', e)
    showToast('error', "La reaction n'a pas ete enregistree")
    if (activeChannelId.value) await loadMessages(activeChannelId.value)
  }
}

// Apollo renvoie des objets figes : ecrire target.reactions leve
// "Cannot assign to read only property". On remplace donc l objet du tableau
// par une copie, ce qui declenche la reactivite de Vue sans toucher a l objet
// d origine. Le cache est mis a jour a part, pour qu une lecture ulterieure
// ne ressorte pas un etat perime.
const applyReactions = (messageId: number, reactions: { id: number; employeeId: string; emoji: string }[]) => {
  const key = String(messageId)
  const next = [...reactions]

  apolloClient.cache.modify({
    id: 'ChatMessage:' + key,
    fields: { reactions: () => next },
  })

  const idx = messages.value.findIndex((m) => String(m.id) === key)
  if (idx < 0) {
    console.warn('[chat] reaction recue pour un message absent de la liste', messageId)
    return
  }
  // Apollo gele le tableau autant que les objets : ni splice ni push ne
  // fonctionnent dessus. On affecte donc un nouveau tableau, ce qui declenche
  // la reactivite de Vue et laisse la reponse d origine intacte.
  messages.value = messages.value.map((m, i) =>
    i === idx ? { ...m, reactions: next } : m,
  )
}

const deliveryState = (message: any) => {
  const receipts = message.receipts ?? []
  if (receipts.length === 0) return 'SENT'
  if (receipts.some((r: any) => r.readAt)) return 'READ'
  if (receipts.some((r: any) => r.viewedAt)) return 'VIEWED'
  return 'SENT'
}

const deliveryLabel = (message: any) =>
  ({ SENT: 'Envoyée', VIEWED: 'Vue', READ: 'Lu' }[deliveryState(message)] ?? 'Envoyée')

// Qui est en train d ecrire dans la conversation ouverte.
// Chaque entree expire d elle-meme : aucune synchronisation d horloge necessaire.
// index : channelId -> (employeeId -> expiration)
const typingIn = ref<Record<number, Record<string, number>>>({})

// Date.now() seul ne reactive pas un computed : sans ce tic d horloge, un
// indicateur qui a expire resterait affiche jusqu au prochain evenement.
const nowTick = ref(Date.now())
let typingClock: ReturnType<typeof setInterval> | null = null

const memberName = (employeeId: string) =>
  employees.value.find((e: any) => e.id === employeeId)?.name ?? 'Quelqu un'

const currentUserName = computed(() => authStore.user?.name ?? '')

// Noms de ceux qui ecrient dans un canal donne, canal ouvert ou non.
const typingInChannel = (channelId: number): string[] => {
  const perChannel = typingIn.value[channelId] ?? {}
  const now = nowTick.value
  return Object.entries(perChannel)
    .filter(([employeeId, expiresAt]) => employeeId !== currentUserId.value && expiresAt > now)
    .map(([employeeId]) => memberName(employeeId))
    .filter(Boolean)
}

const typingLabel = (names: string[]) => {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} et ${names[1]}`
  return `${names.length} personnes`
}

const sendSocket = (payload: Record<string, unknown>) => {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload))
}

const activeTypingNames = computed(() => typingInChannel(activeChannelId.value ?? -1))

// Diffusion de la saisie, limitee a une fois toutes les 2 s.
let lastTypingSentAt = 0
const notifyTyping = () => {
  const el = draftEl.value
  if (!el || !activeChannelId.value || !draft.value.trim()) return
  const now = Date.now()
  if (now - lastTypingSentAt < 2000) return
  lastTypingSentAt = now
  sendSocket({ type: 'chat.typing', channelId: activeChannelId.value, name: currentUserName.value })
}

const activeChannel = computed(() => channels.value.find((c) => c.id === activeChannelId.value) || null)

const gql = async (query: any, variables: Record<string, unknown>) => {
  const result = await apolloClient.query({ query, variables, fetchPolicy: 'network-only' })
  return result.data as any
}

const loadChannels = async () => {
  if (!authStore.token) return
  const data = await gql(CHAT_CHANNELS_QUERY, {})
  channels.value = data.chatChannels
  unreadCount.value = Number(data.unreadChatMessageCount) || 0
}

// Lu = l utilisateur a defile jusqu au message le plus recent.
let lastReadChannel: { channelId: number; at: number } | null = null
const markReadWhenAtBottom = async (channelId: number) => {
  await nextTick()
  const el = messagesEl.value
  if (!el) return
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48
  if (!atBottom) return
  const now = Date.now()
  if (lastReadChannel?.channelId === channelId && now - lastReadChannel.at < 1500) return
  lastReadChannel = { channelId, at: now }
  await apolloClient.mutate({ mutation: MARK_CHAT_CHANNEL_READ, variables: { channelId } }).catch(() => undefined)
}

const loadingOlder = ref(false)

const onMessagesScroll = async () => {
  if (!activeChannelId.value) return
  markReadWhenAtBottom(activeChannelId.value)
  const el = messagesEl.value
  // proche du sommet : on remonte dans l historique
  if (!el || el.scrollTop > 120 || loadingOlder.value || !hasMore.value) return
  loadingOlder.value = true
  const previousHeight = el.scrollHeight
  const previousTop = el.scrollTop
  try {
    await loadMessages(activeChannelId.value, messages.value[messages.value.length - 1]?.id)
    await nextTick()
    // on decale la vue du meme nombre de pixels pour rester sur le meme message
    if (el) el.scrollTop = previousTop + (el.scrollHeight - previousHeight)
  } finally {
    loadingOlder.value = false
  }
}

const scrollToBottom = async () => {
  await nextTick()
  if (messagesEl.value) messagesEl.value.scrollTop = messagesEl.value.scrollHeight
}

// Rendu du plus ancien au plus recent. Le tableau reste en ordre inverse
// (comme la reponse du serveur) pour que la pagination par curseur reste triviale.
const visibleMessages = computed(() => [...messages.value].reverse())

const loadMessages = async (channelId: number, cursor?: number) => {
  const data = await gql(CHAT_MESSAGES_QUERY, { channelId, cursor: cursor ?? null })
  const incoming = (data.chatMessages || []) as any[]
  if (cursor) {
    messages.value = [...incoming, ...messages.value]
    hasMore.value = incoming.length >= 30
  } else {
    messages.value = incoming
    hasMore.value = incoming.length >= 30
    await scrollToBottom()
  }
}

const selectChannel = async (channelId: number) => {
  activeChannelId.value = channelId
  draft.value = ''
  ephemeral.value = false
  emojiOpen.value = false
  await loadMessages(channelId)
  await markReadWhenAtBottom(channelId)
  await loadChannels()
}

const backToList = () => {
  emojiOpen.value = false
  activeChannelId.value = null
  messages.value = []
  loadChannels().catch(() => undefined)
}

const loadMore = () => {
  const oldest = messages.value[messages.value.length - 1]
  if (oldest) loadMessages(activeChannelId.value!, oldest.id).catch(() => undefined)
}

const send = async () => {
  const body = draft.value.trim()
  if (!body || !activeChannelId.value || sending.value) return
  sending.value = true
  try {
    await apolloClient.mutate({ mutation: SEND_CHAT_MESSAGE, variables: { channelId: activeChannelId.value, body, isEphemeral: ephemeral.value } })
    draft.value = ''
    ephemeral.value = false
    emojiOpen.value = false
    await loadMessages(activeChannelId.value)
    await loadChannels()
  } catch {
    showToast({ type: 'error', message: 'Envoi impossible' })
  } finally {
    sending.value = false
  }
}

const openWidget = () => { messenger.show(); loadChannels().catch(() => undefined) }
const minimizeWidget = () => { emojiOpen.value = false; aiOpen.value = false; messenger.hide(); activeChannelId.value = null; messages.value = [] }
const closeWidget = () => { emojiOpen.value = false; aiOpen.value = false; messenger.hide(); activeChannelId.value = null; messages.value = [] }

const openModal = (mode: 'group' | 'direct') => {
  modalMode.value = mode
  groupName.value = ''
  employeeQuery.value = ''
  selectedEmployees.value = []
  modalOpen.value = true
}

const selectableEmployees = computed(() => {
  const query = employeeQuery.value.trim().toLowerCase()
  return employees.value
    .filter((e: any) => e.isActive !== false && e.id !== currentUserId.value)
    .filter((e: any) => !query || e.name.toLowerCase().includes(query))
})

const toggleEmployee = (id: string) => {
  selectedEmployees.value = selectedEmployees.value.includes(id)
    ? selectedEmployees.value.filter((value) => value !== id)
    : [...selectedEmployees.value, id]
}

const createChannel = async () => {
  if (creating.value) return
  if (modalMode.value === 'group' && !groupName.value.trim()) { showToast({ type: 'error', message: 'Le nom du groupe est requis' }); return }
  if (!selectedEmployees.value.length) { showToast({ type: 'error', message: 'Sélectionnez au moins un membre' }); return }
  creating.value = true
  try {
    const result: any = await apolloClient.mutate({
      mutation: CREATE_CHAT_CHANNEL,
      variables: { type: modalMode.value === 'group' ? 'GROUP' : 'DIRECT', name: modalMode.value === 'group' ? groupName.value.trim() : null, employeeIds: selectedEmployees.value },
    })
    modalOpen.value = false
    await loadChannels()
    const id = result?.data?.createChatChannel?.id
    if (id) await selectChannel(id)
  } catch {
    showToast({ type: 'error', message: 'Création impossible' })
  } finally {
    creating.value = false
  }
}

let socket: WebSocket | null = null
let poll: ReturnType<typeof setInterval> | null = null

// Rafraichissement temps reel : on fusionne les nouveaux messages sans effacer
// l historique deja charge, et on ne defile que si l utilisateur etait deja en bas.
const refresh = async () => {
  await loadChannels().catch(() => undefined)
  const channelId = activeChannelId.value
  if (!open.value || !channelId) return
  const el = messagesEl.value
  const wasNearBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 120 : true
  const previousHeight = el?.scrollHeight ?? 0
  try {
    const data = await gql(CHAT_MESSAGES_QUERY, { channelId, cursor: null })
    const incoming = (data.chatMessages || []) as any[]
    const known = new Set(messages.value.map((m) => m.id))
    const fresh = incoming.filter((m) => !known.has(m.id))
    if (fresh.length === 0) return
    messages.value = [...fresh, ...messages.value]
    hasMore.value = incoming.length >= 30
    await nextTick()
    if (wasNearBottom) {
      scrollToBottom()
    } else if (el) {
      // on compense la hauteur ajoutee pour que la vue ne saute pas
      el.scrollTop = el.scrollHeight - previousHeight + el.scrollTop
    }
  } catch {
    // une erreur de rafraichissement ne doit pas interrompre la session
  }
}

onMounted(async () => {
  await Promise.all([loadChannels().catch(() => undefined), loadEmployees().catch(() => undefined)])
  if (authStore.token) {
    const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/ws/notifications?token=${encodeURIComponent(authStore.token)}`
    socket = new WebSocket(wsUrl)
    socket.onmessage = (event) => {
      let payload: any = null
      try { payload = JSON.parse(event.data) } catch { payload = null }
      if (payload?.type === 'chat.message') {
        // Son de message uniquement, jamais pour ce qu on vient d envoyer soi-meme.
        if (payload.senderId !== currentUserId.value) playMessageSound()
        refresh().catch(() => undefined)
        return
      }
      if (payload?.type === 'notification.created') {
        playNotificationSound()
        refresh().catch(() => undefined)
        return
      }
      if (payload?.type === 'chat.reaction' && typeof payload.messageId === 'number') {
        // L evenement porte deja l etat complet des reactions : on l applique
        // directement. Une requete ici ajouterait un aller-retour reseau, et la
        // reaction n apparaitrait qu au retour de celle-ci.
        if (Array.isArray(payload.reactions)) applyReactions(payload.messageId, payload.reactions)
        return
      }
      if (payload?.type === 'chat.typing' && typeof payload.channelId === 'number') {
        const perChannel = { ...(typingIn.value[payload.channelId] ?? {}) }
        perChannel[payload.employeeId] = Date.now() + 4000
        typingIn.value = { ...typingIn.value, [payload.channelId]: perChannel }
        // purge differee pour que le badge ne reste pas fige
        setTimeout(() => {
          const now = Date.now()
          const cleaned = Object.fromEntries(
            Object.entries(typingIn.value[payload.channelId] ?? {}).filter(([, exp]) => exp > now),
          )
          typingIn.value = { ...typingIn.value, [payload.channelId]: cleaned }
        }, 4200)
        return
      }
      refresh().catch(() => undefined)
    }
    socket.onclose = () => { socket = null }
  }
  poll = setInterval(() => { refresh().catch(() => undefined) }, 60000)
  // horloge de l indicateur de saisie : 1 s suffit pour relancer a temps
  typingClock = setInterval(() => { nowTick.value = Date.now() }, 1000)
})

onBeforeUnmount(() => {
  socket?.close()
  if (poll) clearInterval(poll)
  if (typingClock) clearInterval(typingClock)
})
</script>

<style scoped>
/* Trois points en ligne qui se relaient : le decalage entre chaque point cree
   l'illusion d'un mouvement continu, plus lisible qu'une pulsation unique. */
.typing-dots {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 10px;
}
.typing-dots > i {
  width: 4px;
  height: 4px;
  border-radius: 9999px;
  background-color: currentColor;
  animation: typing-bounce 1.1s ease-in-out infinite;
}
.typing-dots > i:nth-child(2) { animation-delay: 0.15s; }
.typing-dots > i:nth-child(3) { animation-delay: 0.3s; }

/* Dans la conversation, l'indicateur est deja visible : il anime en permanence.
   Dans la liste il est plus discret, on ne l'anime que si le canal est ouvert. */
.typing-dots > i { animation-play-state: paused; opacity: 0.45; }
.typing-dots-active > i { animation-play-state: running; opacity: 1; }
p .typing-dots > i { animation-play-state: running; opacity: 1; }

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
}

@media (prefers-reduced-motion: reduce) {
  .typing-dots > i { animation: none !important; }
}
</style>
